import React, { useState, useRef, useEffect } from 'react';
import Select, { components, SingleValue } from 'react-select';
import styled from 'styled-components';
import Pagination from '../components/pagination/Pagination';
import InquiryService from '../../service/inquiry/inquiry';
import Loading from '../../func/components/loading/LoadingCircle';

type EmailOption = {
  label: string;
  value: string;
};

type Inquiry = {
  inquiryId: number;
  category: string;
  content: string;
  createdAt: string;
  email: string;
  answer?: string;
  answeredAt?: string;
};

const emailOptions: EmailOption[] = [
  { label: 'naver.com', value: 'naver.com' },
  { label: 'gmail.com', value: 'gmail.com' },
  { label: 'hanmail.net', value: 'hanmail.net' },
  { label: 'daum.net', value: 'daum.net' },
  { label: 'kakao.com', value: 'kakao.com' },
  { label: '직접 입력', value: 'custom' },
];

const inquiryTypeOptions = [
  { value: '오류', label: '서비스 오류 제보' },
  { value: '기능', label: '이용/기능 문의' },
  { value: '결제', label: '결제/요금제 문의' },
  { value: '환불', label: '요금제 환불 문의' },
  { value: '계정', label: '계정/로그인 문의' },
  { value: '기타', label: '기타' },
];

const customStyles = {
  control: (base: any) => ({
    ...base,
    textAlign: 'left',
    borderRadius: '8px',
    borderColor: '#E1E2E4',
    height: '44px',
    fontSize: '14px',
    fontFamily: 'Spoqa Han Sans Neo',
    marginBottom: '8px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& svg': {
      width: '16px',
      height: '16px',
    },
    fontWeight: 500,
    letterSpacing: '-0.28px',
    color: '#222222',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#222222',
    fontWeight: '500',
  }),
  placeholder: (base: any) => ({
    ...base,
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '-0.28px',
    color: 'rgb(97, 97, 97)',
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingLeft: '16px',
    height: '100%',
    flex: 1,
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: 0,
    color: '#616161',
    width: '16px',
    height: '16px',
    zIndex: 10,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingRight: '16px',
  }),
  option: (base: any, state: any) => ({
    ...base,
    color: '#222222',
    padding: '12px 16px',
    backgroundColor: state.isSelected ? '#0000000A' : 'transparent',
    ':hover': {
      backgroundColor: '#0000000A',
    },
    borderRadius: '8px',
  }),
  menu: (base: any) => ({
    ...base,
    padding: '8px',
    borderRadius: '8px',
    zIndex: 12,
    marginBottom: '8px',
    fontWeight: 500,
  }),
};

const OneOnOneInquiry = () => {
  const customDomainInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'inquiry' | 'myInquiries'>(
    'inquiry',
  );
  const [loading, setLoading] = useState(false);
  const [emailId, setEmailId] = useState('');
  const [emailIdError, setEmailIdError] = useState('');
  const [emailDomainSelect, setEmailDomainSelect] = useState('custom');
  const [customDomain, setCustomDomain] = useState('');
  const [customDomainError, setCustomDomainError] = useState('');
  const [inquiryText, setInquiryText] = useState('');
  const [inquiryTextError, setInquiryTextError] = useState('');
  const [customFocus, setCustomFocus] = useState(false);
  const [selectedType, setSelectedType] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedTypeError, setSelectedTypeError] = useState('');
  const [inquiryList, setInquiryList] = useState<Inquiry[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [valid, setValid] = useState(false);
  const pageSize = 5;
  const messageIcon = '/assets/images/icon/message_icon.svg';
  const dropDownIcon = '/assets/images/arrow/Arrow_bottom.svg';
  const selectedEmailOption =
    emailOptions.find((opt) => opt.value === emailDomainSelect) ??
    emailOptions[emailOptions.length - 1];

  const handleInput = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let valid = true;

    // 이메일 유효성 검사
    // const fullEmail =
    //   emailDomainSelect === 'custom'
    //     ? `${emailId}@${customDomain}`
    //     : `${emailId}@${emailDomainSelect}`;

    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // if (!emailId || (emailDomainSelect === 'custom' && !customDomain.trim())) {
    //   setEmailIdError('이메일을 입력해 주세요.');
    //   setCustomDomainError('이메일을 입력해 주세요.');
    //   valid = false;
    // } else if (!emailRegex.test(fullEmail)) {
    //   setEmailIdError('올바르지 않은 이메일 형식입니다.');
    //   setCustomDomainError('');
    //   valid = false;
    // } else {
    //   setEmailIdError('');
    //   setCustomDomainError('');
    // }

    // 유형 선택 유효성 검사
    if (!selectedType) {
      setSelectedTypeError('문의 유형을 필수로 선택해주세요.');
      valid = false;
    } else {
      setSelectedTypeError('');
    }

    // 문의 내용 유효성 검사
    if (!inquiryText || inquiryText.trim() === '') {
      setInquiryTextError('문의 내용은 필수로 입력해야 합니다.');
      valid = false;
    } else {
      setInquiryTextError('');
    }

    // 유효성 검사 통과 시 폼 제출
    if (valid) {
      setLoading(true);
      try {
        // const domain =
        //   emailDomainSelect === 'custom' ? customDomain : emailDomainSelect;
        // const email = `${emailId}@${domain}`;
        const category = selectedType?.label ?? '';
        const content = inquiryText;

        const data = {
          // email,
          // email: 'test@gmail.com',
          category,
          content,
        };
        await InquiryService.inquiry(data);
        alert('문의가 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.');

        setActiveTab('myInquiries');

        setEmailId('');
        setCustomDomain('');
        setSelectedType(null);
        setInquiryText('');
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('문의 접수에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   const isEmailValid =
  //     emailId !== '' && (emailDomainSelect !== 'custom' || customDomain !== '');
  //   const isContentValid = inquiryText.trim().length > 0;
  //   const isTypeValid = selectedType !== null;

  //   setValid(isEmailValid && isContentValid && isTypeValid);
  // }, [emailId, emailDomainSelect, customDomain, inquiryText, selectedType]);
  useEffect(() => {
    // 이메일 조건 제거
    const isContentValid = inquiryText.trim().length > 0;
    const isTypeValid = selectedType !== null;

    setValid(isContentValid && isTypeValid);
  }, [inquiryText, selectedType]);

  {
    /* 내 문의내역 서버 연결 */
  }
  const fetchMyInquiries = async () => {
    try {
      const res = await InquiryService.fetchMyInquiries(
        '2024-01-01',
        '2025-12-31',
      );
      if (res.status === 'success') {
        const list = res.data.list.map((item: any) => ({
          inquiryId: item.inquiry_id,
          category: item.category,
          content: item.content,
          createdAt: item.created_at,
          email: item.email,
          answer: item.answer,
          answeredAt: item.answer_time,
          processedYn: item.processed_yn,
        }));

        for (const inquiry of list) {
          if (inquiry.answer && inquiry.processedYn !== 'Y') {
            await updateInquiryStatus(inquiry.inquiryId.toString(), {
              status: 'done',
            });
          }
        }

        list.sort((a: Inquiry, b: Inquiry) => b.inquiryId - a.inquiryId);

        setInquiryList(list);
      } else {
        console.warn('서버 응답 실패:', res.message);
      }
    } catch (err: any) {
      console.error('요청 실패:', err?.response?.data);
    }
  };

  const updateInquiryStatus = async (inquiryId: string, payload: any) => {
    try {
      const res = await InquiryService.updateInquiryStatus(inquiryId, payload);

      if (res.success) {
        console.log('문의 상태 업데이트 완료');
        fetchMyInquiries();
      } else {
        console.warn('업데이트 실패:', res.message);
      }
    } catch (err: any) {
      console.error('PATCH 실패:', err?.response?.data);
    }
  };

  useEffect(() => {
    if (activeTab === 'myInquiries') {
      fetchMyInquiries();
    }
  }, [activeTab]);

  const toggleDropdown = (inquiryId: number) => {
    setOpenId((prev) => (prev === inquiryId ? null : inquiryId));
  };

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(inquiryList.length / pageSize);

  const paginatedData = inquiryList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  return (
    <div style={{ alignSelf: 'stretch', width: '100%', padding: '0 0 40px' }}>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div style={{ padding: '0 16px' }}>
            <TabWrapper>
              <TabButton
                active={activeTab === 'inquiry'}
                onClick={() => setActiveTab('inquiry')}
              >
                1:1 문의하기
              </TabButton>
              <TabButton
                active={activeTab === 'myInquiries'}
                onClick={() => setActiveTab('myInquiries')}
              >
                내 문의내역
              </TabButton>
            </TabWrapper>
          </div>
          {activeTab === 'inquiry' && (
            <form
              onSubmit={handleSubmit}
              style={{ margin: '16px auto 0', padding: '16px' }}
            >
              {/* <StyledLabel htmlFor="email">답변 받으실 이메일</StyledLabel>
              <div style={{ marginBottom: '24px' }}>
                <EmailWrapper>
                  <StyledInput
                    type="text"
                    placeholder="이메일 주소"
                    value={emailId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmailId(value);

                      if (!value) {
                        setEmailIdError('이메일을 입력해 주세요.');
                      } else if (!/^[a-zA-Z0-9._]+$/.test(value)) {
                        setEmailIdError('올바르지 않은 이메일 형식입니다.');
                      } else {
                        setEmailIdError('');
                      }
                    }}
                  />
                  <AtSymbol>@</AtSymbol>
                  <DomainInputWrapper>
                    <Select<EmailOption>
                      value={selectedEmailOption}
                      onChange={(option: SingleValue<EmailOption>) => {
                        const selectedValue = option?.value ?? '';
                        setEmailDomainSelect(selectedValue);
                        if (selectedValue === 'custom') {
                          setTimeout(() => {
                            customDomainInputRef.current?.focus();
                          }, 0);
                        }

                        if (customDomainError && selectedValue !== 'custom') {
                          setCustomDomainError('');
                        }

                        if (
                          emailIdError &&
                          emailId !== '' &&
                          (selectedValue !== 'custom' ||
                            (selectedValue === 'custom' &&
                              customDomain.trim() !== ''))
                        ) {
                          setEmailIdError('');
                        }
                      }}
                      options={emailOptions}
                      styles={customStyles}
                      isSearchable={false}
                      placeholder="도메인을 선택하세요"
                      components={{
                        IndicatorSeparator: () => null,
                        DropdownIndicator: components.DropdownIndicator,
                      }}
                    />
                    {emailDomainSelect === 'custom' && (
                      <OverlapInput
                        ref={customDomainInputRef}
                        type="text"
                        placeholder="직접 입력"
                        value={customDomain}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomDomain(value);

                          if (customDomainError && value.trim()) {
                            setCustomDomainError('');
                          }

                          if (
                            emailDomainSelect === 'custom' &&
                            emailIdError &&
                            emailId !== '' &&
                            /^[a-zA-Z0-9._]+$/.test(emailId) &&
                            value.trim()
                          ) {
                            setEmailIdError('');
                          }
                        }}
                        onFocus={() => setCustomFocus(true)}
                        onBlur={() => setCustomFocus(false)}
                        customFocus={customFocus}
                      />
                    )}
                  </DomainInputWrapper>
                </EmailWrapper>
                {(emailIdError || customDomainError) && (
                  <ErrorContainer>
                    <img
                      src="/assets/images/icon/message_icon.svg"
                      alt="메시지 아이콘"
                    />
                    <ErrorText>{emailIdError || customDomainError}</ErrorText>
                  </ErrorContainer>
                )}
              </div> */}

              <StyledLabel htmlFor="type">문의 유형</StyledLabel>
              <div style={{ marginBottom: '24px' }}>
                <Select
                  inputId="type"
                  components={{ IndicatorSeparator: () => null }}
                  options={inquiryTypeOptions}
                  placeholder="유형 선택"
                  isSearchable={false}
                  styles={customStyles}
                  value={selectedType}
                  onChange={(option) => {
                    setSelectedType(option);
                    if (selectedTypeError && option) {
                      setSelectedTypeError('');
                    }
                  }}
                />
                {selectedTypeError && (
                  <ErrorContainer>
                    <img
                      src="/assets/images/icon/message_icon.svg"
                      alt="메시지 아이콘"
                    />
                    <ErrorText>{selectedTypeError}</ErrorText>
                  </ErrorContainer>
                )}
              </div>

              <div style={{ marginBottom: '32px' }}>
                <StyledLabel htmlFor="content">문의 내용</StyledLabel>
                <StyledTextarea
                  id="content"
                  value={inquiryText}
                  ref={textareaRef}
                  onInput={handleInput}
                  placeholder="땅집고옥션 이용 중에 생긴 불편한 사항이나 문의사항을 남겨주세요. 답변은 마이페이지 > 1:1 문의하기 > 내 문의내역 에서 확인할 수 있습니다."
                  maxLength={1000}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInquiryText(value);
                    if (value.trim()) setInquiryTextError('');
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    {inquiryTextError ? (
                      <ErrorContainer>
                        <img src={messageIcon} alt="메시지 아이콘" />
                        <ErrorText>{inquiryTextError}</ErrorText>
                      </ErrorContainer>
                    ) : (
                      <div style={{ visibility: 'hidden' }}>
                        <ErrorText>placeholder</ErrorText>
                      </div>
                    )}
                  </div>
                  <div>
                    <span
                      style={{
                        color: '#616161',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      {inquiryText.length}/1000
                    </span>
                  </div>
                </div>
              </div>

              <StyledButton as="button" $valid={valid}>
                문의하기
              </StyledButton>
            </form>
          )}

          {activeTab === 'myInquiries' && (
            <Wrapper>
              {paginatedData.map((inquiry) => {
                const isAnswered = !!inquiry.answer;
                const isOpen = openId === inquiry.inquiryId;

                return (
                  <Item key={inquiry.inquiryId}>
                    <div style={{ padding: '0 16px' }}>
                      <Row
                        isOpen={isOpen}
                        answered={isAnswered}
                        onClick={() => toggleDropdown(inquiry.inquiryId)}
                      >
                        <StatusRow>
                          <Status answered={isAnswered} isOpen={isOpen}>
                            {isAnswered ? '답변 완료' : '답변 대기중'}
                          </Status>
                          <CreatedAtText>{inquiry.createdAt}</CreatedAtText>
                        </StatusRow>
                        <CategoryRow>{inquiry.category}</CategoryRow>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <SummaryWrap>
                            {' '}
                            <Summary>
                              <OneLineText isOpen={isOpen}>
                                {inquiry.content}
                              </OneLineText>
                              {/* {isOpen && (
                                <Email>
                                  <span style={{ color: '#616161' }}>
                                    이메일{' '}
                                  </span>
                                  {inquiry.email}
                                </Email>
                              )} */}
                            </Summary>
                            <DropdownIcon isOpen={isOpen}>
                              <img
                                src={dropDownIcon}
                                alt=""
                                style={{ width: '20px', height: '20px' }}
                              />
                            </DropdownIcon>
                          </SummaryWrap>
                        </div>
                      </Row>
                    </div>
                    {isOpen && isAnswered && (
                      <Answer>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <CategoryRow>답변 내용</CategoryRow>
                          <CreatedAtText>{inquiry.answeredAt}</CreatedAtText>
                        </div>
                        <AnswerText>{inquiry.answer}</AnswerText>
                      </Answer>
                    )}
                  </Item>
                );
              })}
              <div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </Wrapper>
          )}
        </>
      )}
    </div>
  );
};

export default OneOnOneInquiry;

const TabWrapper = styled.div`
  margin: 8px 0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  background: #70737c0d;
  padding: 4px;
  border-radius: 8px;
`;

const TabButton = styled.button<{ active?: boolean }>`
  cursor: pointer;
  border: none;
  padding: 7px 12px;
  border-radius: 8px;
  background: ${({ active }) => (active ? '#FFFFFF' : 'none')};
  color: ${({ active }) => (active ? '#222222' : '#989BA2')};
  box-shadow: ${({ active }) =>
    active
      ? '0px 1px 4px rgba(56, 58, 59, 0.08),0px 0px 1px rgba(56, 58, 59, 0.08),0px 2px 8px rgba(56, 58, 59, 0.12)'
      : 'none'};
  width: 50%;
  height: 36px;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const StyledLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #616161;
  margin-bottom: 8px;
  letter-spacing: -0.32px;
  line-height: 145%;
`;

const DomainInputWrapper = styled.div`
  position: relative;
  height: 44px;
  width: 46%;
  &:focus {
    border: 1px solid #008aff;
  }
`;

const sharedInputStyle = `
  width: 100%;
  height: 100%;
  border: 1px solid #37383C29;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  outline: none;

    &::placeholder {
    color: #37383C29;
  }
`;

const StyledInput = styled.input`
  ${sharedInputStyle}
  width: 46%;
  font-weight: 500;
  color: #222;
  &::placeholder {
    color: rgba(55, 56, 60, 0.16);
  }
  &:focus {
    border: 1px solid #008aff;
  }
  font-size: 14px;
`;

const OverlapInput = styled.input<{ customFocus: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 12px;
  border: ${({ customFocus }) =>
    customFocus ? '1px solid #008aff' : '1px solid #E1E2E4'};
  box-shadow: ${({ customFocus }) =>
    customFocus ? '0 0 0 1px #008aff' : 'none'};
  border-radius: 8px;
  font-size: 14px;
  color: #222;
  background: white;
  z-index: 2;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-weight: 500;
  &::placeholder {
    color: rgba(55, 56, 60, 0.16);
  }
`;

const StyledTextarea = styled.textarea`
  ${sharedInputStyle}
  height: auto;
  min-height: 124px;
  max-height: 224px;
  line-height: 23.2px;
  letter-spacing: -0.32px;
  font-size: 14px;
  font-weight: 500;
  color: #222222;
  resize: none;
  &:focus {
    border: 1px solid #008aff;
  }
  &::placeholder {
    color: #616161;
  }
`;

export const EmailWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  width: 100%;
  flex-direction: row;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-direction: row;
`;

const ErrorText = styled.p`
  color: red;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

export const AtSymbol = styled.span`
  height: 44px;
  display: flex;
  align-items: center;
  font-size: 16px;
`;

const StyledButton = styled.button<{ $valid: boolean }>`
  padding: 12px 28px;
  width: 100%;
  border: none;
  background: ${({ $valid }) => ($valid ? '#008AFF' : '#F7F7F8')};
  color: ${({ $valid }) => ($valid ? '#fff' : '#37383C29')};
  font-weight: 500;
  font-size: 16px;
  text-align: center;
  border-radius: 8px;
  cursor: ${({ $valid }) => ($valid ? 'pointer' : 'default')};
  transition:
    background 0.3s,
    color 0.3s;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Item = styled.div`
  box-sizing: border-box;
`;

const Row = styled.div<{ answered: boolean; isOpen: boolean }>`
  display: flow-root;
  padding: 16px 0;
  position: relative;
  border-bottom: ${({ isOpen }) =>
    isOpen ? '1px solid #E1E2E4' : '1px solid #E1E2E4'};
  box-sizing: border-box;
  cursor: pointer;
`;

const StatusRow = styled.div`
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Status = styled.span<{ answered: boolean; isOpen: boolean }>`
  display: flex;
  justify-content: center;
  align-self: center;
  flex-direction: column;
  color: ${({ answered }) => (answered ? '#008aff' : '#616161')};
  background: ${({ answered }) => (answered ? '#EAF2FE' : '#70737C14')};
  padding: 5px 8px;
  box-sizing: border-box;
  width: 83px;
  font-weight: 500;
  line-height: 20.3px;
  letter-spacing: -0.28px;
  font-size: 14px;
  text-align: center;
  border-radius: 4px;
`;

const OneLineText = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  width: calc(100% - 30px);
  line-height: 23.2px;
  letter-spacing: -0.32px;
  font-size: 14px;
  font-weight: 500;
  color: #222222;
  display: -webkit-box;
  -webkit-line-clamp: ${({ isOpen }) => (isOpen ? 'unset' : 1)};
  -webkit-box-orient: vertical;
`;

const Summary = styled.div`
  color: #444;
  font-size: 16px;
  font-weight: 500;
  width: 100%;
`;

const CreatedAtText = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  height: 20px;
  color: #616161;
`;

const Email = styled.div`
  margin-top: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #222222;
  flex-direction: row;
  gap: 8px;
`;

const Answer = styled.div`
  background-color: #f9f9f9;
  padding: 16px 16px 0;
  position: relative;
  top: -1px;
`;

const AnswerText = styled.div`
  font-size: 14px;
  color: #222;
  padding-bottom: 16px;
  font-weight: 500;
  white-space: pre-wrap;
  border-bottom: 1px solid #e1e2e4;
`;

const CategoryRow = styled.div`
  width: 180px;
  margin-bottom: 8px;
  font-weight: 700;
`;

const DropdownIcon = styled.div<{ isOpen: boolean }>`
  position: absolute;
  right: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.3s ease;
  }
`;

const SummaryWrap = styled.div`
  display: flex;
  width: 100%;
`;
